import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Button,
  Col,
  DatePicker,
  Empty,
  Form,
  Row,
  Space,
  Table,
  Tooltip,
  message,
  theme,
  Skeleton,
  Select
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter, faRefresh, faCalendarCheck } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'

// Reusable & Core Components
import { ContainerCard } from '../../../../reusable-components/AgiliteCards'
import PageHeader from '../../../../reusable-components/PageHeader'
import TeamChatForm from '../../../../TeamChat/components/team-chat-form'
import ReassignmentModal from '../ReAssignmentModal'
import PatientExaminationForm from '../../ExaminationWrapper'

// Local Components
import { PaymentMethodModal, BookingsFilter, SearchInput, TableColumns } from './components'

// Custom Hooks
import { useDebounce, useBookingsData } from './hooks'

// Utilities & Reducers
import { updateBooking } from '../../../utils/utils'
import { updateAllBookingsState } from '../../../../lib/booking-utils'
import { spinAnimation, buttonStyle, isTodaySelected, sortByDateTime, generateSkeletonColumns } from './utils'
import { executeBooking, getBookingState } from '../../../utils/bpm-utils'
import { handleError } from '../../../../lib/utils'
import { readSystemUsersAggregate } from '../../../../Admin/system-users/utils/utils'
import { readMedicalProfessionalUsers } from '../../../../Admin/medical-professionals/utils/utils'
import CoreEnums from '../../../../../core/utils/enums'
import coreReducer from '../../../../../core/utils/reducer'
import { readClinics } from '../../../../Admin/clinics/utils/utils'

const EmployeeBookingsListView = ({ userRef, isBilling, viewingPatientProfile, readOnly = false }) => {
  const dispatch = useDispatch()
  const authState = useSelector((state) => state.auth)
  const billingStateData = useSelector((state) => state.billing?.data || [])
  const employeeBookingsStateData = useSelector((state) => state.employeeBookings?.data || [])

  // State for UI actions (modals, individual button presses), distinct from main data loading
  const [isActionLoading, setIsActionLoading] = useState(false)

  const [paymentMethodChange, setPaymentMethodChange] = useState(false)
  const [currentRecord, setCurrentRecord] = useState(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [rangeFilter, setRangeFilter] = useState([dayjs(), dayjs()])
  const [facilityFilter, setFacilityFilter] = useState('')
  const [facilities, setFacilities] = useState([])
  const [reAssignment, setReAssignment] = useState({ record: '', profession: '', professionals: [], open: false })
  const [teamChatProps, setTeamChatProps] = useState({ isOpen: false, userRef: '' })
  const [filteredValues, setFilteredValues] = useState({})
  const [showFilters, setShowFilters] = useState(false)
  const [clinicRecord, setClinicRecord] = useState(null)
  const [statuses, setStatuses] = useState([])
  const [changingStatus, setChangingStatus] = useState({})
  const [additionalFilters, setAdditionalFilters] = useState({
    status: [
      CoreEnums.bookingStatuses.checkin,
      CoreEnums.bookingStatuses.screening,
      CoreEnums.bookingStatuses.consultation,
      CoreEnums.bookingStatuses.reception
    ],
    type: ['virtual', 'standard'],
    service: [],
    hideCompleted: false,
    hideCancelled: true,
    nonScheduledOnly: false
  })

  const [additionalFiltersForm] = Form.useForm()
  const { token } = theme.useToken()
  const { RangePicker } = DatePicker

  const {
    loading: dataLoading, // Renamed to avoid conflict, this is for main data skeleton
    total,
    currentUserData,
    paymentMethods,
    services,
    handleGetAllBookings
  } = useBookingsData({
    userRef,
    isBilling,
    viewingPatientProfile,
    rangeFilter,
    additionalFilters,
    setAdditionalFilters,
    searchQuery,
    filteredValues,
    page,
    pageSize,
    setPage,
    facilityFilter
  })

  const fetchFacilities = async () => {
    try {
      const facilities = await readClinics({ entityRef: { $in: authState.agiliteUser?.extraData.entities } })
      setFacilities(facilities)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const fetchClinicRecord = async () => {
    try {
      const clinicRecord = await readClinics({ _id: authState.agiliteUser?.extraData.clinics[0] })
      setClinicRecord(clinicRecord[0])
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  useEffect(() => {
    fetchFacilities()
    fetchClinicRecord()
    // eslint-disable-next-line
  }, [authState.agiliteUser?.extraData.entities])

  /**
   * Fetches available booking statuses from BPM profiles
   * These statuses are used to populate the status dropdown in the table
   */
  const handleGetStatuses = async () => {
    let statuses = []

    try {
      if (clinicRecord?.bpmConfig) {
        clinicRecord.bpmConfig.forEach((statusEntry) => {
          statuses.push({
            key: statusEntry.key,
            label: statusEntry.label,
            value: statusEntry.key
          })
        })
      }

      setStatuses(statuses)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  /**
   * Handles status change for a booking
   * Updates the booking status in the database and executes the BPM workflow
   * @param {string} statusValue - The new status value
   * @param {string} stepKey - The BPM step key
   * @param {object} record - The booking record
   */
  const handleChangeStatus = async (statusValue, stepKey, record) => {
    let processRecord = null
    const bookingId = record._id

    setChangingStatus({ ...changingStatus, [bookingId]: true })

    try {
      await updateBooking({ status: statusValue }, { _id: bookingId })
      processRecord = await getBookingState([record.processRef])

      await executeBooking(
        'patient_journey',
        record.processRef,
        'submit',
        `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`,
        processRecord.key,
        stepKey
      )
      message.success('Status changed successfully.')

      // Refresh the bookings list to show the updated status
      handleGetAllBookings()
    } catch (e) {
      message.error(handleError(e, true))
    } finally {
      setChangingStatus({ ...changingStatus, [bookingId]: false })
    }
  }

  useEffect(() => {
    handleGetStatuses()
    // eslint-disable-next-line
  }, [clinicRecord])

  const rangePresets = [
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] }
  ]

  const handleSearchChange = (searchValue) => {
    if (searchValue) {
      setPage(1)
      setSearchQuery(searchValue)
    } else {
      setSearchQuery('')
      handleGetAllBookings()
    }
  }

  const debouncedHandleSearch = useDebounce((value) => {
    handleSearchChange(value)
  }, 1000)

  const handleSearch = (e) => {
    const value = e.target.value
    debouncedHandleSearch(value)
  }

  const handleOpenBooking = async (record, skipScreening) => {
    if (readOnly) {
      message.info('You do not have permission to perform this action')
      return
    }
    setIsActionLoading(true)
    try {
      let mainMemberProfile = null
      let workflowHistory = {}
      let processRecord = null
      let newRecord = null

      if (skipScreening) {
        workflowHistory = await getBookingState([record.processRef])
        processRecord = await executeBooking(
          'patient_journey',
          record.processRef,
          'submit',
          `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`,
          workflowHistory.key,
          'consultation'
        )
        newRecord = await updateBooking(
          { status: record.clinicRecord.bpmConfig?.find((e) => e.key === processRecord.key)?.key },
          { _id: record._id }
        )
        workflowHistory = processRecord
        workflowHistory.history.splice(0, 1)
        dispatch(updateAllBookingsState(newRecord)) // Dispatch update to Redux
      } else {
        if (record.processRef) {
          workflowHistory = await getBookingState([record.processRef])
          workflowHistory.history.splice(0, 1)
        }
      }
      if (record.patientRecord.mainMemberId) {
        mainMemberProfile = await readSystemUsersAggregate({ _id: record.patientRecord.mainMemberId })
      }
      dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.MY_BOOKINGS}_${record._id}`,
          closable: true,
          label: `Booking: ${record.patientName}`,
          children: (
            <PatientExaminationForm
              data={{
                ...record,
                ...newRecord,
                mainMemberProfile: mainMemberProfile ? mainMemberProfile[0] : undefined
              }}
              refreshView={handleGetAllBookings}
              workflowHistory={workflowHistory}
            />
          )
        })
      )
    } catch (e) {
      message.error(handleError(e, true))
    }
    setIsActionLoading(false)
  }

  const handleReassignment = async (record, profession) => {
    if (readOnly) {
      message.info('You do not have permission to perform this action')
      return
    }
    setIsActionLoading(true)
    try {
      const professionals = await readMedicalProfessionalUsers({
        'extraData.profession': profession === 'doctor' ? 'doctor' : 'nurse',
        'extraData.clinics': record.clinicRef
      })
      setReAssignment({ record, professionals, open: true, profession })
    } catch (e) {
      message.error(handleError(e, true))
    }
    setIsActionLoading(false)
  }

  const handleSetPaymentMethod = (record) => {
    if (readOnly) {
      message.info('You do not have permission to perform this action')
      return
    }
    setPaymentMethodChange(true)
    setCurrentRecord(record)
  }

  const handleChangePaymentMethod = async () => {
    if (readOnly) {
      message.info('You do not have permission to perform this action')
      return
    }
    setIsActionLoading(true)
    try {
      await updateBooking({ paymentMethod: selectedPaymentMethod }, { _id: currentRecord._id })
      message.success('Payment Method Changed Successfully')
      setPaymentMethodChange(false)
      setCurrentRecord(null)
      setSelectedPaymentMethod('')
      handleGetAllBookings()
    } catch (e) {
      message.error(handleError(e, true))
    }
    setIsActionLoading(false)
  }

  const handleClaimAppointment = async (record) => {
    if (readOnly) {
      message.info('You do not have permission to perform this action')
      return
    }
    setIsActionLoading(true)
    try {
      await updateBooking({ medicalProfRef: authState.agiliteUser._id }, { _id: record._id })
      message.success('Appointment claimed successfully')
      handleGetAllBookings()
    } catch (e) {
      message.error(handleError(e, true))
    }
    setIsActionLoading(false)
  }

  return (
    <>
      <style>{spinAnimation}</style>
      <style>{`
        .status-select-colored .ant-select-selector {
          background-color: transparent !important;
          border: none !important;
          color: #fff !important;
        }
        .status-select-colored .ant-select-selection-item {
          color: #fff !important;
          padding-right: 20px !important;
        }
        .status-select-colored .ant-select-arrow {
          color: #fff !important;
        }
        .status-select-colored.ant-select-disabled .ant-select-selector {
          opacity: 0.7;
        }
      `}</style>
      <PaymentMethodModal
        paymentMethodChange={paymentMethodChange}
        setPaymentMethodChange={setPaymentMethodChange}
        currentRecord={currentRecord}
        setCurrentRecord={setCurrentRecord}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        paymentMethods={paymentMethods}
        handleChangePaymentMethod={handleChangePaymentMethod}
      />

      <ContainerCard bodyStyle={{ padding: 0 }}>
        <PageHeader title={isBilling ? 'Billing Management' : 'Appointments Management'} icon={faCalendarCheck} />

        <div style={{ padding: '16px 24px' }}>
          <div style={{ marginBottom: '12px' }}>
            <Row gutter={[0, 0]} align='middle' justify='space-between' wrap>
              <Col xs={24} sm={14} md={16} lg={16} xl={16}>
                <Space wrap>
                  <SearchInput handleSearch={handleSearch} loading={dataLoading} token={token} />
                  <Button
                    disabled={dataLoading || isActionLoading}
                    onClick={() => {
                      if (isTodaySelected(rangeFilter)) return
                      setRangeFilter([dayjs(), dayjs()])
                    }}
                    style={{
                      ...buttonStyle,
                      background: isTodaySelected(rangeFilter) ? token.actionColor : `${token.actionColor}15`,
                      borderColor: isTodaySelected(rangeFilter) ? token.actionColor : `${token.actionColor}30`,
                      color: isTodaySelected(rangeFilter) ? '#ffffff' : token.actionColor,
                      fontWeight: isTodaySelected(rangeFilter) ? 600 : 500
                    }}
                  >
                    Today
                  </Button>
                  <RangePicker
                    disabled={dataLoading || isActionLoading}
                    value={rangeFilter}
                    presets={rangePresets}
                    onChange={(value) => setRangeFilter(value || [])}
                    style={{ height: '40px', borderRadius: '6px' }}
                    allowClear
                  />
                  {authState.agiliteUser?.extraData.role.type === 'admin' && (
                    <Select
                      options={[
                        { label: 'All Facilities', value: '' },
                        ...facilities.map((facility) => ({
                          label: facility.name,
                          value: facility._id
                        }))
                      ]}
                      value={facilityFilter}
                      onChange={(value) => setFacilityFilter(value)}
                      style={{ height: '40px', borderRadius: '6px', width: '300px' }}
                      disabled={dataLoading || isActionLoading}
                      placeholder='Select Facility'
                    />
                  )}
                </Space>
              </Col>
              <Col xs={24} sm={10} md={8} lg={8} xl={8}>
                <Row gutter={[12, 8]} justify='end'>
                  {!isBilling && (
                    <Col>
                      <Tooltip title='Filter bookings'>
                        <Button
                          icon={<FontAwesomeIcon icon={faFilter} />}
                          onClick={() => setShowFilters(!showFilters)}
                          disabled={dataLoading || isActionLoading}
                          type='default'
                          size='large'
                          style={{
                            ...buttonStyle,
                            borderRadius: '6px',
                            background: 'rgba(24, 144, 255, 0.1)',
                            borderColor: 'rgba(24, 144, 255, 0.3)',
                            color: token.actionColor
                          }}
                        >
                          Filter
                        </Button>
                      </Tooltip>
                    </Col>
                  )}
                  <Col>
                    <Tooltip title='Refresh booking list'>
                      <Button
                        icon={
                          <FontAwesomeIcon
                            icon={faRefresh}
                            style={{
                              marginRight: 8,
                              animation: dataLoading || isActionLoading ? 'spin 1s linear infinite' : 'none'
                            }}
                          />
                        }
                        onClick={handleGetAllBookings}
                        disabled={dataLoading || isActionLoading}
                        type='default'
                        size='large'
                        style={{
                          ...buttonStyle,
                          borderRadius: '6px',
                          background: 'rgba(24, 144, 255, 0.1)',
                          borderColor: 'rgba(24, 144, 255, 0.3)',
                          color: token.actionColor
                        }}
                      >
                        Refresh
                      </Button>
                    </Tooltip>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>

          {dataLoading ? (
            <div style={{ padding: '40px 0' }}>
              <Skeleton active paragraph={{ rows: 8 }} title={false} />
            </div>
          ) : (
            <Table
              bordered
              size='small'
              id='booking_list_view'
              rowKey='_id'
              dataSource={
                userRef
                  ? currentUserData
                  : isBilling
                  ? sortByDateTime(billingStateData, 'bookingDate', 'startTime')
                  : sortByDateTime(employeeBookingsStateData, 'bookingDate', 'startTime')
              }
              locale={{
                emptyText: (
                  <Empty
                    description='No Bookings found'
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: '40px 0' }}
                  />
                )
              }}
              onChange={(pagination, filters) => {
                setFilteredValues(filters)
                setPage(pagination.current || 1)
                setPageSize(pagination.pageSize || 50)
              }}
              columns={
                dataLoading
                  ? generateSkeletonColumns(
                      TableColumns({
                        token,
                        userRef,
                        currentUserData,
                        isBilling,
                        billingState: billingStateData,
                        employeeBookingsState: employeeBookingsStateData,
                        filteredValues,
                        handleReassignment,
                        rangeFilter,
                        handleSetPaymentMethod,
                        services,
                        setTeamChatProps,
                        handleOpenBooking,
                        readOnly,
                        updateBooking,
                        handleGetAllBookings,
                        authState,
                        handleClaimAppointment,
                        statuses,
                        handleChangeStatus,
                        changingStatus
                      })
                    )
                  : TableColumns({
                      token,
                      userRef,
                      currentUserData,
                      isBilling,
                      billingState: billingStateData,
                      employeeBookingsState: employeeBookingsStateData,
                      filteredValues,
                      handleReassignment,
                      rangeFilter,
                      handleSetPaymentMethod,
                      services,
                      setTeamChatProps,
                      handleOpenBooking,
                      readOnly,
                      updateBooking,
                      handleGetAllBookings,
                      authState,
                      handleClaimAppointment,
                      isActionLoading,
                      statuses,
                      handleChangeStatus,
                      changingStatus
                    })
              }
              pagination={{
                current: page,
                pageSize,
                total,
                onChange: (p, ps) => {
                  setPage(p)
                  setPageSize(ps)
                },
                showSizeChanger: true,
                hideOnSinglePage: true,
                position: ['bottomCenter'],
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (t) => `Total ${t} bookings`
              }}
              scroll={{ x: 1000 }}
              onRow={() => ({ style: { cursor: 'default', transition: 'background-color 0.3s ease' } })}
            />
          )}
        </div>
      </ContainerCard>

      <BookingsFilter
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        additionalFilters={additionalFilters}
        setAdditionalFilters={setAdditionalFilters}
        additionalFiltersForm={additionalFiltersForm}
        services={services}
        bpmConfig={clinicRecord?.bpmConfig}
      />

      <ReassignmentModal
        reAssignment={reAssignment}
        setReAssignment={setReAssignment}
        handleRefresh={handleGetAllBookings}
      />
      <TeamChatForm modalProps={teamChatProps} setModalProps={setTeamChatProps} />
    </>
  )
}

export default EmployeeBookingsListView
